// useCalendarPeek — the calm right-rail "peek", not a strip.
//
// Fetches today's events (GET /calendar/events) and exposes only the next
// upcoming one + a count of the rest. Tapping "see all" is the caller's job —
// it posts "What's on today?" into the centre chat (the peek pattern from the
// spec / design session), never a permanent calendar strip.

import { useQuery } from '@tanstack/react-query';
import { listCalendarEvents, type CalendarEvent } from '../api/client';

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export interface CalendarPeek {
  /** The next event still upcoming today, if any. */
  next: CalendarEvent | null;
  /** How many further events remain today after `next`. */
  moreCount: number;
  /** All of today's remaining events, in start order. */
  remaining: CalendarEvent[];
  isLoading: boolean;
  /** True when the calendar surface doesn't exist for this user (endpoint
   *  404s — not deployed / nothing to connect). Renders "Not connected". */
  unavailable: boolean;
  /** True on a transient failure (network, 5xx) — the calendar IS set up,
   *  we just couldn't reach it this time. Renders a calm retry line, never
   *  "Not connected". */
  failed: boolean;
}

export function useCalendarPeek(): CalendarPeek {
  const { data, isLoading, error } = useQuery({
    queryKey: ['calendar', 'today'],
    queryFn: () => listCalendarEvents(startOfToday().toISOString(), endOfToday().toISOString()),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const now = Date.now();
  const upcoming = (data?.events ?? [])
    .filter((e) => new Date(e.endAt).getTime() >= now)
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  // The API client throws `Error("<status>: <body>")` (api/client.ts
  // throwForStatus). GET /calendar/events returns 200 + [] even when no
  // provider is connected, so the only "not connected/available" signal is a
  // 404 (route not deployed). Anything else is a transient failure and must
  // not masquerade as "Not connected".
  const notDeployed = !!error && error instanceof Error && /^404\b/.test(error.message);

  return {
    next: upcoming[0] ?? null,
    moreCount: Math.max(0, upcoming.length - 1),
    remaining: upcoming.slice(1),
    isLoading,
    unavailable: notDeployed,
    failed: !!error && !notDeployed,
  };
}
